import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, GridApi } from 'ag-grid-community';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-utilisateurs',
  standalone: true,
  imports: [AgGridAngular, FormsModule, DatePipe],
  templateUrl: './utilisateurs.component.html',
  styleUrl: './utilisateurs.component.scss'
})
export class UtilisateursComponent implements OnInit {
  users: User[] = [];
  isLoading = true;
  searchText = '';
  private gridApi!: GridApi;
  selectedUsers: User[] = [];
  currentDate = new Date();

  columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      width: 80,
      cellStyle: { fontWeight: '600', color: '#718096' }
    },
    {
      headerName: 'Nom complet',
      valueGetter: (params) => `${params.data.prenom} ${params.data.nom}`,
      flex: 1,
      minWidth: 180,
      cellStyle: { fontWeight: '600', color: '#1a202c' }
    },
    {
      headerName: 'Email',
      field: 'email',
      flex: 1,
      minWidth: 200
    },
    {
      headerName: 'Téléphone',
      field: 'tel',
      width: 150,
      valueFormatter: (params) => params.value || '-'
    },
    {
      headerName: 'Profils',
      field: 'profils',
      width: 250,
      valueFormatter: (params) => {
        if (!params.value || params.value.length === 0) return '-';
        return params.value.map((profil: any) => profil.nom).join(', ');
      },
      cellRenderer: (params: any) => {
        if (!params.value || params.value.length === 0) {
          return '<span style="color: #a0aec0;">-</span>';
        }

        const colors: { [key: string]: { bg: string; text: string } } = {
          'super_admin': { bg: '#fce7f3', text: '#be123c' },
          'magasin': { bg: '#dbeafe', text: '#1e40af' },
          'etudiant': { bg: '#d1fae5', text: '#065f46' }
        };

        return params.value.map((profil: any) => {
          const color = colors[profil.code] || { bg: '#e5e7eb', text: '#374151' };
          return `<span style="background: ${color.bg}; color: ${color.text}; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 600; margin-right: 4px; display: inline-block; white-space: nowrap;">${profil.nom}</span>`;
        }).join('');
      }
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  paginationPageSize = 20;
  paginationPageSizeSelector = [10, 20, 50, 100];
  theme: 'legacy' = 'legacy';

  rowSelection = {
    mode: 'multiRow' as const,
    checkboxes: true,
    headerCheckbox: true
  };

  constructor(
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.userService.getAllUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erreur chargement utilisateurs:', err);
        this.isLoading = false;
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  onSearch(): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', this.searchText);
    }
  }

  onSelectionChanged(event: any): void {
    this.selectedUsers = event.api.getSelectedRows();
  }

  formatDate(date: string | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  printSelection(): void {
    let dataToExport: User[] = [];

    if (this.selectedUsers.length > 0) {
      // Si sélection, exporter la sélection
      dataToExport = this.selectedUsers;
    } else {
      // Récupérer toutes les lignes filtrées
      const allFilteredRows: User[] = [];
      this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
        if (node.data) {
          allFilteredRows.push(node.data);
        }
      });

      // Appliquer la pagination pour n'exporter que la page courante
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const pageSize = this.gridApi.paginationGetPageSize();
      const startRow = currentPage * pageSize;
      const endRow = startRow + pageSize;

      dataToExport = allFilteredRows.slice(startRow, endRow);
    }

    if (dataToExport.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    // Créer le PDF
    const doc = new jsPDF();

    // Titre
    doc.setFontSize(18);
    doc.setTextColor(26, 32, 44);
    doc.text('Liste des utilisateurs', 14, 20);

    // Sous-titre
    doc.setFontSize(11);
    doc.setTextColor(74, 85, 104);
    const subtitle = this.selectedUsers.length > 0
      ? `${this.selectedUsers.length} utilisateur(s) sélectionné(s)`
      : `${dataToExport.length} utilisateur(s) affiché(s)`;
    doc.text(subtitle, 14, 28);

    // Date
    doc.setFontSize(9);
    doc.setTextColor(113, 128, 150);
    doc.text(`Imprimé le ${this.formatDate(this.currentDate.toISOString())}`, 14, 35);

    // Préparer les données du tableau
    const tableData = dataToExport.map(user => [
      user.id?.toString() || '-',
      `${user.prenom} ${user.nom}`,
      user.email || '-',
      user.tel || '-',
      user.profils && user.profils.length > 0
        ? user.profils.map((profil: any) => profil.nom).join(', ')
        : '-'
    ]);

    // Générer le tableau
    autoTable(doc, {
      startY: 40,
      head: [['ID', 'Nom complet', 'Email', 'Téléphone', 'Profils']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [247, 250, 252],
        textColor: [26, 32, 44],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [226, 232, 240]
      },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      }
    });

    // Sauvegarder le PDF
    const filename = `utilisateurs_${new Date().getTime()}.pdf`;
    doc.save(filename);
  }
}
